import { SearchPageFilterInput } from '../../__generated__/api/types';

/**
 * Codec for the `?filters=` query parameter.
 *
 * Wire format: `<id>:<value>&<value>,<id>:<value>`
 *
 * Every id and value is individually percent-encoded, so the three structural
 * separators (`,`, `:`, `&`) can never appear inside a payload and the format
 * round-trips for arbitrary ids/values. Decoding is total: malformed input
 * yields fewer filters, never a throw. That matters because the parameter is
 * user-editable and is parsed during module initialisation.
 */

const ENTRY_SEPARATOR = ',';
const ID_SEPARATOR = ':';
const VALUE_SEPARATOR = '&';

export const FILTERS_PARAM = 'filters';

type SearchParamsLike = {
  get(name: string): string | null;
};

function safeDecode(raw: string): string | null {
  if (!raw) return null;
  try {
    return decodeURIComponent(raw);
  } catch {
    // Malformed percent-escape (e.g. a hand-edited URL). Drop the segment.
    return null;
  }
}

export function encodeFiltersToQueryParam(filters: SearchPageFilterInput[]): string {
  return filters
    .filter(filter => filter.id && filter.value.length > 0)
    .map(filter => {
      const id = encodeURIComponent(filter.id);
      const values = filter.value.map(value => encodeURIComponent(value)).join(VALUE_SEPARATOR);
      return `${id}${ID_SEPARATOR}${values}`;
    })
    .join(ENTRY_SEPARATOR);
}

export function decodeFiltersFromQueryParam(
  raw: string | null | undefined
): SearchPageFilterInput[] {
  if (!raw) return [];

  const filters: SearchPageFilterInput[] = [];

  for (const entry of raw.split(ENTRY_SEPARATOR)) {
    const separatorIndex = entry.indexOf(ID_SEPARATOR);
    // `<= 0` also rejects entries with an empty id (`:foo`).
    if (separatorIndex <= 0) continue;

    const id = safeDecode(entry.slice(0, separatorIndex));
    if (!id) continue;

    const value = entry
      .slice(separatorIndex + 1)
      .split(VALUE_SEPARATOR)
      .map(safeDecode)
      .filter((decoded): decoded is string => decoded !== null);

    if (value.length === 0) continue;

    filters.push({ id, value });
  }

  return filters;
}

export function getFiltersInputFromQueryParams({
  searchParams,
}: {
  searchParams: SearchParamsLike | null;
}): SearchPageFilterInput[] {
  return decodeFiltersFromQueryParam(searchParams?.get(FILTERS_PARAM));
}
